import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { movies } from '@/mocks/movies';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category/movie' },
  { label: '电视剧', to: '/category/tv' },
  { label: '综艺', to: '/category/variety' },
  { label: '资讯', to: '/news' },
  { label: '关于', to: '/about' },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  const suggestions = keyword.trim()
    ? movies
        .filter((movie) => {
          const q = keyword.trim().toLowerCase();
          return (
            movie.title.toLowerCase().includes(q) ||
            movie.genres.join(' ').toLowerCase().includes(q) ||
            movie.director.toLowerCase().includes(q)
          );
        })
        .slice(0, 6)
    : [];

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSearchOpen(false);
    setSuggestOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target as Node)) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (mobileSearchOpen) {
      const timer = window.setTimeout(() => mobileInputRef.current?.focus(), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [mobileSearchOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen || mobileSearchOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen, mobileSearchOpen]);

  const isActive = (to: string) => {
    if (to === '/') return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  const submitSearch = (value: string) => {
    const query = value.trim();
    if (!query) return;
    setSuggestOpen(false);
    setMobileSearchOpen(false);
    navigate(`/search?q=${encodeURIComponent(query)}`);
  };

  const renderSuggestions = (onPick: () => void) => (
    <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 overflow-hidden rounded-lg border border-background-200 bg-background-50">
      {suggestions.length > 0 ? (
        <ul>
          {suggestions.map((movie) => (
            <li key={movie.id}>
              <Link
                to={`/detail/${movie.id}`}
                onClick={onPick}
                className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-background-100"
              >
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} 影视详情`}
                  className="h-12 w-9 shrink-0 rounded object-cover"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground-900">
                    {movie.title}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-foreground-500">
                    {movie.year} · {movie.genres.join(' / ')} · {movie.region}
                  </span>
                </span>
                <span className="shrink-0 font-label text-xs font-semibold text-accent-600">
                  {movie.rating.toFixed(1)}
                </span>
              </Link>
            </li>
          ))}
          <li className="border-t border-background-200">
            <button
              type="button"
              onClick={() => submitSearch(keyword)}
              className="flex w-full cursor-pointer items-center justify-between px-3 py-2.5 text-xs text-primary-700 transition-colors hover:bg-primary-50"
            >
              <span>查看「{keyword.trim()}」的全部结果</span>
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </li>
        </ul>
      ) : (
        <p className="px-3 py-4 text-xs text-foreground-500">没有匹配的作品，换个关键词试试</p>
      )}
    </div>
  );

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[70px] md:px-6 lg:px-8">
          <Link to="/" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-900">
              <i className="ri-play-fill text-lg text-accent-400" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-extrabold tracking-wide text-foreground-950">
                黄牛视频
              </span>
              <span className="mt-1 font-label text-[9px] uppercase tracking-[0.22em] text-foreground-400">
                Huangniu TV
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive(item.to)
                    ? 'font-semibold text-primary-700'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                {item.label}
                {isActive(item.to) && (
                  <span className="absolute inset-x-3 -bottom-[1px] h-[2px] rounded-full bg-accent-500" />
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div ref={searchWrapRef} className="relative hidden w-52 lg:block xl:w-72">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submitSearch(keyword);
                }}
              >
                <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100/80 px-3 py-2 transition-colors focus-within:border-primary-300 focus-within:bg-background-50">
                  <i className="ri-search-line text-sm text-foreground-400" />
                  <input
                    value={keyword}
                    onChange={(event) => {
                      setKeyword(event.target.value);
                      setSuggestOpen(true);
                    }}
                    onFocus={() => setSuggestOpen(true)}
                    placeholder="搜索影视作品"
                    aria-label="搜索影视作品"
                    className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                  />
                  <button
                    type="submit"
                    className="shrink-0 cursor-pointer text-xs font-medium text-primary-700 transition-colors hover:text-accent-600"
                  >
                    搜索
                  </button>
                </div>
              </form>
              {suggestOpen && keyword.trim() && renderSuggestions(() => setSuggestOpen(false))}
            </div>

            <button
              type="button"
              aria-label="搜索"
              onClick={() => setMobileSearchOpen(true)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className="ri-search-line text-lg" />
            </button>

            <Link
              to="/news"
              className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-900 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-800 xl:flex"
            >
              <i className="ri-flashlight-line text-base text-accent-400" />
              今日影视动态
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMobileMenuOpen(true)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            >
              <i className="ri-menu-3-line text-xl" />
            </button>
          </div>
        </div>
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 bg-background-50 md:hidden">
          <div className="flex h-16 items-center justify-between px-4">
            <span className="font-heading text-base font-bold text-foreground-950">导航菜单</span>
            <button
              type="button"
              aria-label="关闭菜单"
              onClick={() => setMobileMenuOpen(false)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 hover:bg-background-100"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
          <nav className="flex flex-col px-4 pt-2">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center justify-between border-b border-background-200 py-4 text-base ${
                  isActive(item.to) ? 'font-semibold text-primary-700' : 'text-foreground-800'
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
          </nav>
          <p className="px-4 pt-6 text-xs leading-relaxed text-foreground-500">
            黄牛视频 · 影视资讯与推荐平台。本站内容为示例数据，播放功能为前端演示。
          </p>
        </div>
      )}

      {mobileSearchOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background-50 lg:hidden">
          <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
            <form
              className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-300"
              onSubmit={(event) => {
                event.preventDefault();
                submitSearch(keyword);
              }}
            >
              <i className="ri-search-line text-sm text-foreground-400" />
              <input
                ref={mobileInputRef}
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索电影、电视剧、综艺"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setKeyword('')}
                  className="cursor-pointer text-foreground-400"
                >
                  <i className="ri-close-circle-fill text-sm" />
                </button>
              )}
            </form>
            <button
              type="button"
              onClick={() => setMobileSearchOpen(false)}
              className="cursor-pointer whitespace-nowrap text-sm text-foreground-600"
            >
              取消
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {keyword.trim() ? (
              <div className="relative">{renderSuggestions(() => setMobileSearchOpen(false))}</div>
            ) : (
              <div>
                <p className="mb-3 text-xs font-medium text-foreground-500">大家都在搜</p>
                <div className="flex flex-wrap gap-2">
                  {['长夜将明', '长安旧事', '声浪撞地球', '逆风少年', '悬疑'].map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setKeyword(tag)}
                      className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}